/** Tour creation wizard — 5-step non-linear stepper dialog (console convention:
 *  hand-rolled WizardData + per-step validate, not RHF).
 *
 *  `prefill` opens it filled in — the tour record's **Duplicate** (operator, kind, the
 *  tour as the source, "<name> (copy)", its scheduling, time zone and launch policy) — on
 *  the Basics step, with every step reachable.
 *
 *  `pageTenants` (a list page's New tour, D9) only seeds the operator — pre-selected when
 *  the page names exactly one, featured in the picker — and every open starts over on the
 *  Operator step. Without it the operator starts from the console scope.
 *
 *  A launch policy for a tour of an easy tenant still under the outbound guard is refused
 *  (409 easy_go_live_required): said with Go live in its easy setup, everything entered kept. */
import {
  Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel,
  Radio, RadioGroup, Step, StepButton, Stepper, TextField, ToggleButton,
  ToggleButtonGroup, Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';

import { ConsoleApiError } from '@/api/client';
import * as toursApi from '@/api/tours';
import type { TourCreateInput } from '@/api/tours';
import { RemoteAutocomplete } from '@/components/RemoteAutocomplete/RemoteAutocomplete';
import {
  TenantPicker,
  useResolvedTenant,
  type TenantPickerValue,
} from '@/components/TenantPicker/TenantPicker';
import { singlePageTenant } from '@/scope/pageTenants';
import { getTenantScope } from '@/scope/tenantScope';
import { useSnackbar } from '@/components/Snackbar';
import {
  easyGoLiveLinks,
  easyGoLiveRequired,
  easyGoLiveRequiredWords,
} from '@/features/tour-operators/onboard/easyGoLiveRequired';

export interface WizardData {
  operator: TenantPickerValue | null;
  tripType: number;         // 0 tour, 1 bus (exhibition disabled)
  mode: 'duplicate' | 'scratch';
  source: { id: number; name: string | null } | null;
  name: string;
  timezone: string;
  isBespoke: boolean;
  startDate: string;
  launchPolicy: { id: number; name: string | null } | null;
}

const DEFAULTS: WizardData = {
  operator: null, tripType: 0, mode: 'duplicate', source: null, name: '',
  timezone: '', isBespoke: true, startDate: '', launchPolicy: null,
};

const STEPS = ['Operator & kind', 'Basics', 'Scheduling', 'Policies', 'Review'];

export function TourCreateWizard({
  open,
  onClose,
  prefill,
  pageTenants,
}: {
  open: boolean;
  onClose: () => void;
  /** Open filled in (Duplicate), on the Basics step. */
  prefill?: Partial<WizardData>;
  /** The page's operators (D9): seeds only the operator, never jumps a step. */
  pageTenants?: TenantPickerValue[];
}) {
  const snackbar = useSnackbar();
  const navigate = useNavigate();
  // The operator starts from the page's single tenant (`pageTenants`), else from the
  // console-wide scope; the operator step stays fully editable (and is still
  // required by validate()).
  const [data, setData] = useState<WizardData>(() => ({
    ...DEFAULTS,
    operator: pageTenants !== undefined ? singlePageTenant(pageTenants) : getTenantScope(),
    ...(prefill ?? {}),
  }));
  const [active, setActive] = useState(prefill ? 1 : 0);
  const [maxVisited, setMaxVisited] = useState(prefill ? STEPS.length - 1 : 0);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** The tenants a 409 easy_go_live_required named (they set launch policies with Go live). */
  const [goLiveIds, setGoLiveIds] = useState<number[] | null>(null);
  const patch = (p: Partial<WizardData>) => setData((d) => ({ ...d, ...p }));

  // With page tenants every open starts over on the page's tenant, so nothing a
  // Cancelled run entered carries over (the list page mounts it per open anyway).
  useEffect(() => {
    if (!open || pageTenants === undefined || prefill) return;
    setData({ ...DEFAULTS, operator: singlePageTenant(pageTenants) });
    setActive(0);
    setMaxVisited(0);
    setError(null);
    setGoLiveIds(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  function validate(step: number): string | null {
    if (step === 0) {
      if (!data.operator) return 'Choose a tour operator.';
      if (data.mode === 'duplicate' && !data.source) return 'Choose a source tour to duplicate.';
    }
    if (step === 1 && !data.name.trim()) return 'Enter a name.';
    if (step === 2 && data.isBespoke && !data.startDate) return 'Choose a start date.';
    return null;
  }

  function go(step: number) {
    // moving forward validates the current step
    if (step > active) {
      const err = validate(active);
      if (err) { setError(err); return; }
    }
    setError(null);
    setActive(step);
    setMaxVisited((m) => Math.max(m, step));
  }

  async function submit() {
    for (let s = 0; s < 3; s++) {
      const err = validate(s);
      if (err) { setActive(s); setError(err); return; }
    }
    if (!data.operator) return;
    setSubmitting(true);
    setError(null);
    setGoLiveIds(null);
    const body: TourCreateInput = {
      mode: data.mode,
      tour_operator: data.operator.id,
      name: data.name.trim(),
      is_bespoke: data.isBespoke,
      trip_type: data.tripType,
      ...(data.isBespoke && data.startDate ? { start_date: data.startDate } : {}),
      ...(data.timezone.trim() ? { start_timezone: data.timezone.trim() } : {}),
      ...(data.launchPolicy ? { launch_policy: { id: data.launchPolicy.id } } : {}),
      ...(data.mode === 'duplicate' && data.source ? { source_id: data.source.id } : {}),
    };
    try {
      const tour = await toursApi.create(body);
      snackbar.show('Tour created.', 'success');
      onClose();
      navigate(`/tours/${tour.id}`);
    } catch (e) {
      const goLive = easyGoLiveRequired(e);
      if (goLive) {
        setGoLiveIds(goLive.tourOperatorIds.length > 0 ? goLive.tourOperatorIds : [data.operator.id]);
        return;
      }
      setError(e instanceof ConsoleApiError ? (e.body?.detail ?? e.message) : 'Creation failed.');
    } finally {
      setSubmitting(false);
    }
  }

  const opId = data.operator?.id;
  // A page tenant arrives nameless; the summary reads the picker's label lookup.
  const operatorShown = useResolvedTenant(data.operator);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{prefill?.source ? `Duplicate ${prefill.source.name ?? 'tour'}` : 'New tour'}</DialogTitle>
      <DialogContent dividers>
        <Stepper nonLinear activeStep={active} sx={{ mb: 3 }}>
          {STEPS.map((label, i) => (
            <Step key={label} completed={i < maxVisited && i !== active}>
              <StepButton onClick={() => i <= maxVisited && go(i)}>{label}</StepButton>
            </Step>
          ))}
        </Stepper>

        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
        {goLiveIds && (
          <Alert severity="info" sx={{ mb: 2 }} onClose={() => setGoLiveIds(null)} data-testid="easy-go-live-required">
            The tour was not created. {easyGoLiveRequiredWords(goLiveIds)} To create the tour now,
            leave its launch policy empty on the Policies step so it uses the tenant default.
            <Box component="span" sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 0.5 }}>
              {easyGoLiveLinks(goLiveIds).map((l) => (
                <Button key={l.id} size="small" color="inherit" component={RouterLink} to={l.to} sx={{ px: 0 }}>
                  {l.label}
                </Button>
              ))}
            </Box>
          </Alert>
        )}

        {active === 0 && (
          <Box>
            <Typography variant="caption" color="text.secondary">Operator and kind can&apos;t be changed later.</Typography>
            <Box sx={{ my: 2, maxWidth: 420 }}>
              <TenantPicker label="Tour operator" value={data.operator} featured={pageTenants}
                onChange={(t) => patch({ operator: t, source: null })} />
            </Box>
            <Typography variant="subtitle2" sx={{ mt: 1 }}>Kind</Typography>
            <RadioGroup row value={data.tripType} onChange={(e) => patch({ tripType: Number(e.target.value) })}>
              <FormControlLabel value={0} control={<Radio />} label="Tour (itinerary)" />
              <FormControlLabel value={1} control={<Radio />} label="Bus trip (line set)" />
              <FormControlLabel value={2} control={<Radio disabled />} label="Exhibition (not yet supported)" />
            </RadioGroup>
            <Typography variant="subtitle2" sx={{ mt: 1 }}>Creation mode</Typography>
            <RadioGroup value={data.mode} onChange={(e) => patch({ mode: e.target.value as WizardData['mode'] })}>
              <FormControlLabel value="duplicate" control={<Radio />} label="Duplicate a template / existing tour" />
              <FormControlLabel value="scratch" control={<Radio />} label="From scratch" />
            </RadioGroup>
            {data.mode === 'duplicate' && (
              <Box sx={{ mt: 1, maxWidth: 420 }}>
                <RemoteAutocomplete<{ id: number; name: string | null }>
                  label="Source tour" size="small" fetchOnEmpty value={data.source}
                  getOptionLabel={(o) => o.name ?? `#${o.id}`} getOptionKey={(o) => o.id}
                  queryFn={async (q) => {
                    if (!opId) return [];
                    const r = await toursApi.picker(opId, 'template_tours', q);
                    return r.results;
                  }}
                  onChange={(v) => patch({ source: v as { id: number; name: string | null } | null })}
                />
                <Typography variant="caption" color="text.secondary">
                  Templates are listed; duplication copies the itinerary, tag categories, departures and settings.
                </Typography>
              </Box>
            )}
          </Box>
        )}

        {active === 1 && (
          <Box sx={{ maxWidth: 480 }}>
            <TextField label="Name" value={data.name} onChange={(e) => patch({ name: e.target.value })}
              size="small" fullWidth sx={{ mb: 2 }} autoFocus />
            <TextField label="Timezone" value={data.timezone} onChange={(e) => patch({ timezone: e.target.value })}
              size="small" fullWidth placeholder="Europe/Reykjavik"
              helperText="IANA timezone in which the trip starts." />
          </Box>
        )}

        {active === 2 && (
          <Box>
            <Typography variant="caption" color="text.secondary">Scheduling mode can&apos;t be changed later.</Typography>
            <Box sx={{ my: 2 }}>
              <ToggleButtonGroup exclusive value={data.isBespoke ? 'fixed' : 'recurring'}
                onChange={(_, v) => { if (v) patch({ isBespoke: v === 'fixed' }); }}>
                <ToggleButton value="fixed">Fixed date</ToggleButton>
                <ToggleButton value="recurring">Recurring</ToggleButton>
              </ToggleButtonGroup>
            </Box>
            {data.isBespoke ? (
              <TextField type="date" label="Start date" value={data.startDate}
                onChange={(e) => patch({ startDate: e.target.value })}
                size="small" InputLabelProps={{ shrink: true }} sx={{ width: 220 }} />
            ) : (
              <Alert severity="info">
                Recurring tours run every day by default (no schedule editor). Add departure times after creation.
              </Alert>
            )}
          </Box>
        )}

        {active === 3 && (
          <Box sx={{ maxWidth: 420 }}>
            <RemoteAutocomplete<{ id: number; name: string | null }>
              label="Launch policy (optional)" size="small" fetchOnEmpty value={data.launchPolicy}
              getOptionLabel={(o) => o.name ?? `#${o.id}`} getOptionKey={(o) => o.id}
              queryFn={async (q) => {
                if (!opId) return [];
                const r = await toursApi.picker(opId, 'launch_policies', q);
                return r.results;
              }}
              onChange={(v) => patch({ launchPolicy: v as { id: number; name: string | null } | null })}
            />
            {data.tripType === 1 && (
              <Alert severity="info" sx={{ mt: 2 }}>Link a line set from the Line Set tab after creation.</Alert>
            )}
          </Box>
        )}

        {active === 4 && (
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Review</Typography>
            <Summary label="Operator" value={operatorShown ? `${operatorShown.name} (${operatorShown.code ?? ''})` : '—'} />
            <Summary label="Kind" value={data.tripType === 1 ? 'Bus trip' : 'Tour'} />
            <Summary label="Mode" value={data.mode === 'duplicate' ? `Duplicate of ${data.source?.name ?? '—'}` : 'From scratch'} />
            <Summary label="Name" value={data.name} />
            <Summary label="Timezone" value={data.timezone || '—'} />
            <Summary label="Scheduling" value={data.isBespoke ? `Fixed date · ${data.startDate || '—'}` : 'Recurring (daily)'} />
            <Summary label="Launch policy" value={data.launchPolicy?.name ?? 'Default'} />
            <Alert severity="warning" sx={{ mt: 2 }}>Operator, kind and scheduling mode can&apos;t be changed after creation.</Alert>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Box sx={{ flexGrow: 1 }} />
        <Button disabled={active === 0} onClick={() => go(active - 1)}>Back</Button>
        {active < STEPS.length - 1 ? (
          <Button variant="contained" onClick={() => go(active + 1)}>Next</Button>
        ) : (
          <Button variant="contained" onClick={submit} disabled={submitting}>
            {submitting ? 'Creating…' : 'Create tour'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

function Summary({ label, value }: { label: string; value: string }) {
  return (
    <Box sx={{ display: 'flex', gap: 2, py: 0.5 }}>
      <Typography variant="body2" color="text.secondary" sx={{ minWidth: 120 }}>{label}</Typography>
      <Typography variant="body2">{value}</Typography>
    </Box>
  );
}
